import { useEffect, useMemo, useRef, useState } from 'react';
import { isAbortError } from '../lib/apiErrorHandling';

/**
 * Generic helper hook for resolving entity IDs into a lookup map.
 * It deduplicates IDs, handles abort signalling, and normalizes state updates.
 */
export function useEntityLookup<TKey extends string, TValue>(
  ids: readonly (TKey | null | undefined)[],
  loader: (ids: TKey[], signal: AbortSignal) => Promise<Map<TKey, TValue>>,
): Map<TKey, TValue> {
  const normalizedIdsRef = useRef<{ key: string; ids: TKey[] }>({ key: '', ids: [] });

  const normalizedIds = useMemo(() => {
    const seen = new Set<TKey>();
    const deduped: TKey[] = [];
    ids.forEach((id) => {
      if (!id) {
        return;
      }
      if (!seen.has(id)) {
        seen.add(id);
        deduped.push(id);
      }
    });
    deduped.sort();
    const key = JSON.stringify(deduped);
    if (normalizedIdsRef.current.key === key) {
      return normalizedIdsRef.current.ids;
    }
    normalizedIdsRef.current = { key, ids: deduped };
    return deduped;
  }, [ids]);

  const [lookup, setLookup] = useState<Map<TKey, TValue>>(new Map());

  useEffect(() => {
    if (normalizedIds.length === 0) {
      setLookup(new Map());
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    loader(normalizedIds, controller.signal)
      .then((result) => {
        if (!cancelled && !controller.signal.aborted) {
          setLookup(result);
        }
      })
      .catch((error) => {
        if (controller.signal.aborted || cancelled) {
          return;
        }
        if (isAbortError(error)) {
          return;
        }
        setLookup(new Map());
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [loader, normalizedIds]);

  return lookup;
}
